<script setup lang="ts">
import {computed, ref, onMounted, onUnmounted} from 'vue';

const props = defineProps<{
  size?: string | number;
}>();

const sizeValue = computed(() => {
  if (typeof props.size === 'number') {
    return `${props.size}px`;
  }
  return props.size ?? '80px';
});

const isDark = ref(false);

function checkDarkMode(): void {
  isDark.value =
    document.documentElement.classList.contains('force-dark-mode') ||
    document.body.classList.contains('body--dark') ||
    (!document.documentElement.classList.contains('force-light-mode') &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);
}

let observer: MutationObserver | null = null;

onMounted(() => {
  checkDarkMode();
  observer = new MutationObserver(checkDarkMode);
  observer.observe(document.documentElement, {attributes: true, attributeFilter: ['class']});
  observer.observe(document.body, {attributes: true, attributeFilter: ['class']});
});

onUnmounted(() => {
  observer?.disconnect();
});

const strokeColor = computed(() => isDark.value ? '#D5D5D5' : '#20282E');

// Light theme: gradient reversed (transparent to black), solid at bottom-right
// Dark theme: solid at left (~270deg / 9 o'clock), fades clockwise
// Using 340deg spread so tail only becomes transparent near the head
const maskGradient = computed(() => {
  if (isDark.value) {
    return 'conic-gradient(from 270deg, black 0deg, transparent 340deg)';
  }
  // Light theme: reversed gradient direction
  return 'conic-gradient(from 90deg, transparent 0deg, black 340deg)';
});

// Light theme: spins clockwise, Dark theme: spins counter-clockwise
const animationDirection = computed(() => isDark.value ? 'reverse' : 'normal');
</script>

<template>
  <svg
    class="spinner-tail"
    :width="sizeValue"
    :height="sizeValue"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    :style="{
      mask: maskGradient,
      WebkitMask: maskGradient,
      animationDirection: animationDirection,
    }"
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      :stroke="strokeColor"
      stroke-width="3"
      fill="none"
    />
  </svg>
</template>

<style lang="scss">
.spinner-tail {
  animation: spinner-tail-spin 0.8s linear infinite;
}

// Responsive size for full-page Quasar Loading overlay
.q-loading .spinner-tail {
  width: 50px !important;
  height: 50px !important;

  @media screen and (min-width: 768px) {
    width: 80px !important;
    height: 80px !important;
  }
}

@keyframes spinner-tail-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
